/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { gnoment } from 'shared-ui/gnoment';
import { RequiredValidation } from 'sarsaparilla';

import * as sharedTypes from 'ui-permit/src/constants';
import {
    LOTTERY_PREFERENCE_SELECTION_LIMIT,
    LOTTERY_PREFERENCES_TYPE,
} from 'ui-permit/src/constants';
import { LOTTERY_CHOICES_PROP_PATH } from 'ui-permit/shared-components/constants/propPaths';
import { readRule } from 'ui-permit/src/utils/utilsNew';
import GroupDetailHOC from 'ui-permit/shared-components/registration/GroupDetailNew';
import NeedToKnowHOC from 'ui-permit/shared-components/registration/NeedToKnow';
import GroupSizeInput from 'ui-hunting-permit/src/components/lottery-registration/GroupSizeInput';
import { LOTTERY_PREFERENCE_TYPES } from 'ui-permit/shared-components/constants/rules';
import {
    HuntingPermitProps,
    LotteryProps,
    HuntingLotteryApplicationProps,
} from '../../props';
import {
    databindings,
    maxPreferredDatesMapping,
    readMaxGroupSizeRule,
} from '../../constants/registration';
import PreferredDatesPicker from './PreferredDatesPicker';
import PreferredDivisionGroupSizeList from './multi-division/PreferredDivisionGroupSizeList';
import DNRNumberInput from './DNRNumberInput';

const groupDetail = GroupDetailHOC(HuntingLotteryApplicationProps, databindings);
const dnrNumber = DNRNumberInput;
const groupSizeNumber = GroupSizeInput;
const needToKnow = NeedToKnowHOC(HuntingLotteryApplicationProps, databindings);

const componentMapping = {
    [sharedTypes.BLACKWATER_TURKEY_ID]: [groupDetail, dnrNumber],
    [sharedTypes.BOMBAY_HOOK_ID]: [groupDetail],
    [sharedTypes.PRIME_HOOK_ID]: [groupDetail],
    [sharedTypes.GEORGETOWN_LAKE_ID]: [groupDetail],
    [sharedTypes.BELTON_LAKE_ID]: [groupDetail],
    [sharedTypes.CAROLINA_SANDHILLS_ID]: [groupDetail],
    [sharedTypes.SOMERVILLE_LAKE_ID]: [groupDetail, groupSizeNumber],
    [sharedTypes.GEORGETOWN_WATERFOWL_ID]: [groupDetail],
    [sharedTypes.GEORGETOWN_OTHER_ID]: [groupDetail],
    [sharedTypes.MATTAMUSKEET_NWR_WATERFOWL]: [groupDetail],
    [sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL]: [groupDetail],
};

const afterPermitForm = [
    {
        componentName: 'NeedToKnow',
        component: needToKnow,
    },
];

const propTypes = {
    permit: HuntingPermitProps.isRequired,
    lottery: LotteryProps.isRequired,
    reservation: HuntingLotteryApplicationProps.isRequired,
    seasonYear: PropTypes.number,
    permitTypeID: PropTypes.string,
    validations: PropTypes.shape({
        summary: PropTypes.array,
        displayValidationError: PropTypes.bool,
    }).isRequired,
    updatePendingReservation: PropTypes.func.isRequired,
    formValidationChange: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
};

const LotteryRegistrationForms = React.memo((props) => {
    const {
        permit,
        validations,
        reservation,
        lottery,
        permitTypeID,
        seasonYear,
        updatePendingReservation,
        formValidationChange,
        isLoading,
    } = props;

    const { displayValidationError } = validations;

    const componentProps = {
        isLoading,
        permit,
        reservation,
        permitTypeID,
        displayValidationError,
        validation: validations,
        pActions: {
            updatePendingReservation,
            formValidationChange,
        },
        // TODO when refactored to use shared-ui OrderDetailsLayout, remove this flag
        scrollToTopOnChecked: true,
    };

    const lotteryChoicesPropPath = databindings[LOTTERY_CHOICES_PROP_PATH];
    const lotteryChoices = get(reservation, lotteryChoicesPropPath, []);
    const onPreferredDatesChange = (value) => {
        updatePendingReservation(reservation.id, lotteryChoicesPropPath, value);
    };

    const ruleMaxPreferredDates = readRule(
        LOTTERY_PREFERENCE_SELECTION_LIMIT,
        permit.rules
    );

    const ruleLotteryPreferencesType = readRule(LOTTERY_PREFERENCES_TYPE, permit.rules);

    const maxPreferredDates = !ruleMaxPreferredDates.isMissing
        ? ruleMaxPreferredDates.value // accept a Zero as value
        : maxPreferredDatesMapping[permit.id] || 3; // TODO: this must be removed when migrated Hunting to Rules

    const components = componentMapping[permit.id] || [];
    // TODO: figure out better key
    const formComponents = components.map((Component, i) => {
        return <Component {...componentProps} key={`lottery_component_${i}`} />;
    });

    // TODO: this must be removed when migrated Hunting to Rules
    const noPreferredDateLotteryPermits = [
        sharedTypes.GEORGETOWN_LAKE_ID,
        sharedTypes.BELTON_LAKE_ID,
        sharedTypes.CAROLINA_SANDHILLS_ID,
        sharedTypes.SOMERVILLE_LAKE_ID,
        sharedTypes.GEORGETOWN_WATERFOWL_ID,
        sharedTypes.GEORGETOWN_OTHER_ID,
        sharedTypes.MATTAMUSKEET_NWR_WATERFOWL,
        sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL,
    ];

    if (!noPreferredDateLotteryPermits.includes(permit.id) && maxPreferredDates > 0) {
        // TODO: make dates update via redux state
        formComponents.splice(
            2,
            0,
            <PreferredDatesPicker
                key="preferred-dates-picker"
                {...componentProps}
                permitType={permitTypeID}
                seasonYear={seasonYear}
                seasonStart={get(lottery, 'start_date', null)}
                seasonEnd={get(lottery, 'end_date', null)}
                onChange={onPreferredDatesChange}
                divisions={permit ? permit.divisions : {}}
                dates={lotteryChoices}
                max={maxPreferredDates}
            />
        );
    }

    // handle special permits cases
    if (
        permit.id === sharedTypes.CAROLINA_SANDHILLS_ID ||
        permit.id === sharedTypes.MATTAMUSKEET_NWR_WATERFOWL ||
        permit.id === sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL
    ) {
        const maxGroupSize = readMaxGroupSizeRule(
            permit.id,
            `${permitTypeID}-lottery`,
            permit.rules
        );

        const onChangeDivision = (idx, newValue) => {
            const toChange = newValue;
            const selectedDivision = permit.divisions[toChange.division_id];
            const divisionStartDate = selectedDivision.description_map['start_date'];
            if (divisionStartDate) {
                toChange.preferred_date = gnoment.utc(divisionStartDate);
            } else {
                toChange.preferred_date = '';
            }

            lotteryChoices[idx] = toChange;
            updatePendingReservation(
                reservation.id,
                lotteryChoicesPropPath,
                lotteryChoices
            );
        };

        const onValidatePreference = (isInvalid, error) =>
            formValidationChange(
                `${reservation.id}_${lotteryChoicesPropPath}`,
                isInvalid ? error : null
            );

        const divisionDateRangeOnly =
            ruleLotteryPreferencesType.value === LOTTERY_PREFERENCE_TYPES.DATE_DIVISION;

        const divisionLabel =
            permit.id === sharedTypes.MATTAMUSKEET_NWR_WATERFOWL ||
            permit.id === sharedTypes.MATTAMUSKEET_DISABLED_WATERFOWL
                ? 'Hunt Dates'
                : 'Division';

        const preferredDivisionGroupSizeComponent = (
            <>
                <PreferredDivisionGroupSizeList
                    key="preferred-dates-group-size-picker"
                    {...componentProps}
                    divisions={permit ? permit.divisions : {}}
                    selections={lotteryChoices}
                    maxGroupSize={maxGroupSize}
                    maxChoices={maxPreferredDates}
                    onChange={onChangeDivision}
                    hideGroupSize={divisionDateRangeOnly}
                    preferredDivisionLabel={divisionLabel}
                />
                <RequiredValidation
                    value={
                        lotteryChoices.length > 0 ? lotteryChoices.length.toString() : ''
                    }
                    displayInSummary
                    onValidated={onValidatePreference}
                    controlLabel={`Lottery Preferences`}
                />
            </>
        );
        formComponents.splice(2, 0, preferredDivisionGroupSizeComponent);
    }

    return formComponents;
});

export const LotteryAfterFormComponents = (props) => {
    const {
        permit,
        validations,
        reservation,
        updatePendingReservation,
        formValidationChange,
        isLoading,
    } = props;
    const { displayValidationError } = validations;
    const componentProps = {
        isLoading,
        permit,
        reservation,
        displayValidationError,
        validation: validations,
        pActions: {
            updatePendingReservation,
            formValidationChange,
        },
        scrollToTopOnChecked: true,
    };

    const afterFormComponents = afterPermitForm.map(
        ({ component: OrderComponent }, sindx) => {
            return (
                <OrderComponent {...componentProps} key={`lottery_component_${sindx}`} />
            );
        }
    );

    return afterFormComponents;
};

LotteryRegistrationForms.propTypes = propTypes;
export default LotteryRegistrationForms;
